import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch } from 'react-icons/fa';
import { 
  TextField, 
  Button, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';

const EmployeeEngagementTypesConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [engagementTypes, setEngagementTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Unified modal state
  const [engagementTypeModal, setEngagementTypeModal] = useState({ 
    open: false, 
    isEdit: false, 
    formData: { name: '', code: '' },
    originalId: null
  });
  
  const [deleteModal, setDeleteModal] = useState({ open: false, engagementType: null });

  const fetchEngagementTypes = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getEmployeeEngagementTypes();
      setEngagementTypes(data);
    } catch (err) {
      showAlert('Failed to load employee engagement types.', 'error', err);
      console.error('Error fetching employee engagement types:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEngagementTypes();
  }, []);

  // Unified handler for both add and edit
  const handleSaveEngagementType = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      if (engagementTypeModal.isEdit) {
        // Edit existing engagement type
        if (!engagementTypeModal.originalId) {
          showAlert('Invalid employee engagement type data for update', 'error');
          return;
        }
        
        await organizationApi.updateEmployeeEngagementType(engagementTypeModal.originalId, {
          name: engagementTypeModal.formData.name,
          code: engagementTypeModal.formData.code
        });
        showAlert('Individual engagement type updated successfully', 'success');
      } else {
        // Add new engagement type
        await organizationApi.createEmployeeEngagementType({
          name: engagementTypeModal.formData.name,
          code: engagementTypeModal.formData.code
        });
        showAlert('Individual engagement type added successfully', 'success');
      }
      
      await fetchEngagementTypes();
      // Reset modal state
      setEngagementTypeModal({
        open: false,
        isEdit: false,
        formData: { name: '', code: '' },
        originalId: null
      });
    } catch (err) {
      const action = engagementTypeModal.isEdit ? 'update' : 'add';
      showAlert(err.message || `Failed to ${action} individual engagement type.`, 'error', err);
      console.error(`Error ${action}ing employee engagement type:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEngagementType = (engagementType) => {
    setDeleteModal({ open: true, engagementType });
  };

  const confirmDelete = async () => {
    const typeToDelete = deleteModal.engagementType;
    setDeleteModal({ open: false, engagementType: null });
    
    if (!typeToDelete?.id) {
      showAlert('Invalid individual engagement type data', 'error');
      return;
    }

    try {
      setIsLoading(true);
      await organizationApi.deleteEmployeeEngagementType(typeToDelete.id);
      await fetchEngagementTypes();
      showAlert('Individual engagement type deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting employee engagement type:', err);
      showAlert(err.message || 'Failed to delete individual engagement type.', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredEngagementTypes = engagementTypes.filter(type => {
    if (!searchTerm) return true;
    return type.name.toLowerCase().includes((searchTerm || '').toLowerCase());
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Individual Engagement Types Configuration</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              placeholder="Search Individual Engagement Type"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
              }}
            />
            <HeaderButton onClick={() => setEngagementTypeModal({
              open: true,
              isEdit: false,
              formData: { name: '', code: '' },
              originalId: null
            })}>
              <FaPlus /> Add Individual Engagement Type
            </HeaderButton>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              <Grid 
                container 
                spacing={3}
                sx={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                    lg: 'repeat(4, 1fr)',
                    xl: 'repeat(4, 1fr)'
                  },
                  gap: '24px',
                  '& .MuiGrid-item': {
                    padding: '0',
                    width: '100%'
                  }
                }}
              >
                {filteredEngagementTypes.map((engagementType) => (
                  <Grid item key={engagementType.id}>
                    <Card 
                      sx={{ 
                        width: '100%',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: '1px solid #e0e0e0',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          borderColor: '#bdbdbd',
                          backgroundColor: '#f1f3f5',
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '16px !important',
                        flexGrow: 1,
                      }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 500,
                            color: '#212529',
                            marginBottom: '4px'
                          }}
                        >
                          {engagementType.name}
                        </Typography>

                        {engagementType.code && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#868e96',
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              display: 'block',
                              marginBottom: '8px'
                            }}
                          >
                            Code: {engagementType.code}
                          </Typography>
                        )}

                        {engagementType.employeeCount > 0 && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#868e96',
                              fontStyle: 'italic'
                            }}
                          >
                            Attached to {engagementType.employeeCount} consultant{engagementType.employeeCount !== 1 ? 's' : ''}
                          </Typography>
                        )}
                        
                        <Box sx={{ 
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '8px',
                          marginTop: '16px'
                        }}>
                          <Button
                            onClick={() => setEngagementTypeModal({
                              open: true,
                              isEdit: true,
                              formData: { name: engagementType.name, code: engagementType.code || '' },
                              originalId: engagementType.id
                            })}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#0d6efd',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(13, 110, 253, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(13, 110, 253, 0.15)',
                                color: '#0d6efd'
                              }
                            }}
                          >
                            Edit
                          </Button>

                          <Button
                            onClick={() => handleDeleteEngagementType(engagementType)}
                            sx={{
                              minWidth: 'unset',
                              padding: '6px 12px',
                              fontSize: '14px',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              backgroundColor: 'rgba(220, 53, 69, 0.1)',
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'rgba(220, 53, 69, 0.15)',
                                color: '#dc3545'
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          </MainContainer>
        </div>
      </Content>

      {/* Unified modal for both adding and editing employee engagement types */}
      <Modal
        isOpen={engagementTypeModal.open}
        onClose={() => setEngagementTypeModal({
          open: false,
          isEdit: false,
          formData: { name: '', code: '' },
          originalId: null
        })}
        onSubmit={handleSaveEngagementType}
        title={engagementTypeModal.isEdit ? "Edit Individual Engagement Type" : "Add New Individual Engagement Type"}
        submitText={engagementTypeModal.isEdit ? "Update Individual Engagement Type" : "Add Individual Engagement Type"}
        submitDisabled={!engagementTypeModal.formData.name.trim() || !engagementTypeModal.formData.code.trim()}
      >
        <Box mb={2}>
          <TextField
            id="employee-engagement-type-name"
            name="name"
            label="Individual Engagement Type Name"
            required
            value={engagementTypeModal.formData.name}
            onChange={(e) => setEngagementTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, name: e.target.value }
            }))}
            placeholder="Enter individual engagement type name"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
        <Box mb={2}>
          <TextField
            id="employee-engagement-type-code"
            name="code"
            label="Individual Engagement Type Code"
            required
            value={engagementTypeModal.formData.code}
            onChange={(e) => setEngagementTypeModal(prev => ({
              ...prev,
              formData: { ...prev.formData, code: e.target.value }
            }))}
            placeholder="Enter individual engagement type code (e.g., FTE, CON)"
            autoComplete="off"
            fullWidth
            variant="outlined"
          />
        </Box>
      </Modal>

      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, engagementType: null })}
        onConfirm={confirmDelete}
        title="Delete Individual Engagement Type"
        message={`Are you sure you want to delete ${deleteModal.engagementType?.name}?`}
        actionButtonText="Delete"
      />
    </Container>
  );
};

export default EmployeeEngagementTypesConfiguration; 